import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { isValidCpf } from '@ibk/shared-utils';
import { PixType } from '../../enums';
import { formatPixKeyAlphanumeric, PixType as FormatPixType } from '../../utils/formatPixKey';
import { formatPhoneNumber, unformatValue } from '../../utils/pixFormatters';
import { validateEmail, validatePhoneNumber } from '../../utils/pixValidations';
export const usePixKeyValidation = (keyType, value) => {
    const { t } = useTranslation();
    const { isValidCnpj, maskCnpj, unmaskCnpj, isAlphanumericEnabled } = useCnpjAlphanumeric();
    const [isTouched, setIsTouched] = useState(false);
    const validateEmailType = useCallback((inputValue) => {
        if (!inputValue.trim()) {
            return {
                isValid: false,
                error: t('pix-keys.validation.email-required'),
                formattedValue: inputValue,
            };
        }
        if (!validateEmail(inputValue.trim())) {
            return {
                isValid: false,
                error: t('pix-keys.validation.email-invalid'),
                formattedValue: inputValue,
            };
        }
        return {
            isValid: true,
            formattedValue: inputValue.trim(),
        };
    }, [t]);
    const validatePhoneType = useCallback((inputValue) => {
        const unformattedPhone = unformatValue(inputValue);
        if (!unformattedPhone) {
            return {
                isValid: false,
                error: t('pix-keys.validation.phone-required'),
                formattedValue: inputValue,
            };
        }
        if (!validatePhoneNumber(inputValue)) {
            return {
                isValid: false,
                error: t('pix-keys.validation.phone-invalid'),
                formattedValue: formatPhoneNumber(inputValue),
            };
        }
        return {
            isValid: true,
            formattedValue: formatPhoneNumber(inputValue),
        };
    }, [t]);
    const validateTaxIdentifierType = useCallback((inputValue) => {
        const helpers = { isValidCnpj, maskCnpj, unmaskCnpj };
        const canonical = helpers.unmaskCnpj(inputValue);
        if (!canonical) {
            return {
                isValid: false,
                error: t('pix-keys.validation.tax-identifier-required'),
                formattedValue: inputValue,
            };
        }
        const isValid = isValidCpf(inputValue) || helpers.isValidCnpj(inputValue);
        const formattedValue = formatPixKeyAlphanumeric(inputValue, FormatPixType.TAX_IDENTIFIER, helpers);
        if (!isValid) {
            return {
                isValid: false,
                error: t('pix-keys.validation.tax-identifier-invalid'),
                formattedValue,
            };
        }
        return {
            isValid: true,
            formattedValue,
        };
    }, [t, isValidCnpj, maskCnpj, unmaskCnpj]);
    const validateRandomType = useCallback(() => {
        return {
            isValid: true,
            formattedValue: '',
        };
    }, []);
    const getValidationByType = useCallback((type, inputValue) => {
        const validationMap = {
            [PixType.EMAIL]: () => validateEmailType(inputValue),
            [PixType.PHONE]: () => validatePhoneType(inputValue),
            [PixType.TAX_IDENTIFIER]: () => validateTaxIdentifierType(inputValue),
            [PixType.RANDOM]: () => validateRandomType(),
        };
        const validator = validationMap[type];
        if (validator) {
            return validator();
        }
        return {
            isValid: false,
            error: t('pix-keys.validation.invalid-key-type'),
            formattedValue: inputValue,
        };
    }, [t, validateEmailType, validatePhoneType, validateTaxIdentifierType, validateRandomType]);
    const validationResult = useMemo(() => {
        if (keyType === PixType.RANDOM) {
            return {
                isValid: true,
                formattedValue: '',
            };
        }
        if (!isTouched && value.trim() === '') {
            return {
                isValid: false,
                formattedValue: value,
            };
        }
        return getValidationByType(keyType, value);
    }, [keyType, value, isTouched, getValidationByType]);
    const markAsTouched = useCallback(() => {
        setIsTouched(true);
    }, []);
    const getPlaceholder = useCallback((type) => {
        const placeholderMap = {
            [PixType.EMAIL]: 'exemplo@exemplo.com',
            [PixType.PHONE]: '(99) 99999-9999',
            [PixType.TAX_IDENTIFIER]: isAlphanumericEnabled
                ? t('pix-keys.creation.placeholder-example-alphanumeric-cnpj-mask')
                : '000.000.000-00',
            [PixType.RANDOM]: '',
        };
        return placeholderMap[type] ?? '';
    }, [isAlphanumericEnabled, t]);
    const getInputType = useCallback((type) => {
        const inputTypeMap = {
            [PixType.EMAIL]: 'email',
            [PixType.PHONE]: 'tel',
            [PixType.TAX_IDENTIFIER]: 'text',
            [PixType.RANDOM]: 'text',
        };
        return inputTypeMap[type] ?? 'text';
    }, []);
    const isDisabled = useCallback((type) => {
        return type === PixType.TAX_IDENTIFIER || type === PixType.RANDOM;
    }, []);
    return {
        validationResult,
        getPlaceholder,
        getInputType,
        isDisabled,
        markAsTouched,
    };
};
